/**
 * 페이지별 Open Graph / Twitter Card 메타 태그 및 document.title 업데이트.
 * Detail 페이지에서 데이터 로드 후 호출하면 SNS 공유 시 해당 페이지 이미지/제목이 노출되도록 합니다.
 *
 * 참고: Facebook, 카카오, 네이버 등 일부 SNS 봇은 최초 HTML만 읽을 수 있어
 * JS로 바꾼 메타가 반영되지 않을 수 있습니다. 100% 보장이 필요하면
 * 서버에서 URL별로 og 메타가 포함된 HTML을 반환하는 방식(SSR 또는 OG 전용 API)을 고려하세요.
 */

const DEFAULT_TITLE = "링꾹라이브 - 서브컬처 라이브공연/아티스트 플랫폼";
const DEFAULT_DESCRIPTION =
  "링꾹라이브는 서브컬처 라이브공연/아티스트 플랫폼입니다. 라이브공연/아티스트 정보를 검색하고 찜할 수 있습니다.";
const DEFAULT_IMAGE = "https://live.linkook.it/favicon3.ico";
const DEFAULT_URL = "https://live.linkook.it/";
const PRODUCTION_HOST = "live.linkook.it";
const SITE_NAME = "링꾹라이브";
const SITE_TAGLINE = "서브컬처 라이브공연/아티스트 플랫폼";

/** 공유용 URL을 항상 https 절대 URL로 맞춤 (상대 경로·http 혼동 방지) */
function ensureHttpsUrl(url) {
  if (!url || typeof url !== "string") return DEFAULT_URL;
  const s = url.trim();
  if (s.startsWith("https://")) return s;
  if (s.startsWith("http://")) {
    try {
      const u = new URL(s);
      if (u.hostname === PRODUCTION_HOST) return `https://${u.hostname}${u.pathname}${u.search}${u.hash}`;
    } catch {
      /* invalid URL, fall through */
    }
    return s;
  }
  if (typeof window !== "undefined" && window.location) {
    const origin = window.location.origin;
    const secureOrigin = origin.startsWith("http://") && window.location.hostname === PRODUCTION_HOST
      ? `https://${window.location.hostname}`
      : origin;
    return s.startsWith("/") ? `${secureOrigin}${s}` : `${secureOrigin}/${s}`;
  }
  return s.startsWith("/") ? `https://${PRODUCTION_HOST}${s}` : DEFAULT_URL;
}

function ensureMeta(attribute, value, isProperty = true) {
  const attr = isProperty ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${attribute}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, attribute);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value || "");
  return el;
}

/**
 * @param {Object} options
 * @param {string} [options.title] - 페이지 제목 (document.title + og:title)
 * @param {string} [options.description] - 설명 (og:description, description)
 * @param {string} [options.image] - 대표 이미지 절대 URL (og:image, twitter:image)
 * @param {string} [options.url] - 페이지 절대 URL (og:url). 없으면 현재 location 사용
 */
export function setPageMeta(options = {}) {
  const raw = options.title && options.title.trim() ? options.title.trim() : null;
  const title = raw ? `[링꾹라이브] ${raw} - 서브컬처 라이브공연/아티스트 플랫폼` : DEFAULT_TITLE;
  const pageDesc = options.description && options.description.trim() ? options.description.trim() : null;
  const description =
    pageDesc && pageDesc !== DEFAULT_DESCRIPTION
      ? `${pageDesc} · ${SITE_NAME} - ${SITE_TAGLINE}`
      : pageDesc || DEFAULT_DESCRIPTION;
  const image = options.image && options.image.startsWith("http") ? options.image : DEFAULT_IMAGE;
  const rawUrl = options.url || (typeof window !== "undefined" ? window.location.href : DEFAULT_URL);
  const url = ensureHttpsUrl(rawUrl);

  if (typeof document === "undefined") return;

  document.title = title;

  ensureMeta("og:title", title);
  ensureMeta("og:description", description);
  ensureMeta("og:image", image);
  ensureMeta("og:url", url);
  ensureMeta("og:site_name", SITE_NAME);

  ensureMeta("twitter:card", "summary_large_image", false);
  ensureMeta("twitter:title", title, false);
  ensureMeta("twitter:description", description, false);
  ensureMeta("twitter:image", image, false);

  ensureMeta("description", description, false);
}

/**
 * 메타와 타이틀을 사이트 기본값으로 되돌립니다.
 * 다른 페이지로 이동할 때 호출할 수 있습니다 (선택).
 */
export function resetPageMeta() {
  setPageMeta({
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    image: DEFAULT_IMAGE,
    url: DEFAULT_URL,
  });
}

/**
 * 현재 문서 head의 og 메타에서 공유용 payload 읽기.
 * @returns {{ title: string, description: string, image: string, url: string }}
 */
export function getPageMetaFromDocument() {
  if (typeof document === "undefined") {
    return { title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION, image: DEFAULT_IMAGE, url: DEFAULT_URL };
  }
  const getContent = (selector) => document.querySelector(selector)?.getAttribute("content") || "";
  const rawUrl = getContent('meta[property="og:url"]') || (window.location && window.location.href) || DEFAULT_URL;
  const url = ensureHttpsUrl(rawUrl);
  return {
    title: getContent('meta[property="og:title"]') || document.title || DEFAULT_TITLE,
    description: getContent('meta[property="og:description"]') || DEFAULT_DESCRIPTION,
    image: getContent('meta[property="og:image"]') || DEFAULT_IMAGE,
    url,
  };
}

/**
 * 트위터(X) 글쓰기 화면을 연다. 제목+URL을 미리 채워 넣음. (이미지 카드는 트위터 봇이 URL을 크롤링할 때 나옴)
 */
export function shareViaTwitter() {
  const { title, url } = getPageMetaFromDocument();
  const text = title ? `${title}\n${url}` : url;
  const intentUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
  if (typeof window !== "undefined") {
    window.open(intentUrl, "_blank", "noopener,noreferrer,width=550,height=420");
  }
}

/** 데스크톱이면 true. 데스크톱에서는 kakaolink 스킴을 처리할 앱이 없어 intent 오류가 난다. */
export function isDesktop() {
  if (typeof navigator === "undefined") return true;
  return !/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * 카카오톡 기본 템플릿(피드)으로 공유. 이미지/제목/설명이 미리보기에 그대로 반영됨.
 * 데스크톱에서는 시도하지 않고 false를 반환해 시스템 공유/링크 복사로 넘긴다.
 * @returns {Promise<boolean>} 카카오 공유 창을 띄웠으면 true, 실패 또는 미지원 시 false
 */
export function shareViaKakao() {
  if (typeof window === "undefined" || !window.Kakao || !window.Kakao.Share) {
    return Promise.resolve(false);
  }
  if (isDesktop()) {
    return Promise.resolve(false);
  }
  const { title, description, image, url } = getPageMetaFromDocument();
  const feed = {
    objectType: "feed",
    content: {
      title: title || "링꾹라이브",
      description: description || "",
      imageUrl: image && image.startsWith("http") ? image : DEFAULT_IMAGE,
      link: {
        webUrl: url,
        mobileWebUrl: url,
      },
    },
  };
  try {
    const result = window.Kakao.Share.sendDefault(feed);
    if (result != null && typeof result.then === "function") {
      return result.then(() => true).catch(() => false);
    }
    return Promise.resolve(true);
  } catch (e) {
    return Promise.resolve(false);
  }
}
